import { onUnmounted, watch } from "vue"
import { registerModalBackHandler } from "../stores/uiStore"

/**
 * Keep a hardware-back handler registered while `isActive` is true.
 *
 * `handler` may return `false` to decline the press, which leaves it
 * registered and lets the press fall through (see uiStore.dismissTopModal).
 * Multi-step surfaces use that to step backwards through their own history and
 * then hand the last press back to the app.
 */
export function useHardwareBack(isActive: () => boolean, handler: () => boolean | void): void {
  let unregister: (() => void) | null = null

  watch(
    isActive,
    (active) => {
      if (active && !unregister) {
        unregister = registerModalBackHandler(handler)
      } else if (!active && unregister) {
        unregister()
        unregister = null
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    unregister?.()
    unregister = null
  })
}

/**
 * Keep an overlay registered on the modal back-handler stack while it is
 * open, so the Android hardware back button closes it instead of navigating
 * away (see uiStore.dismissTopModal, consumed in App.vue).
 *
 * @param isOpen getter for the overlay's open state (usually the modelValue prop)
 * @param close  the overlay's own close routine
 */
export function useModalBackClose(isOpen: () => boolean, close: () => void): void {
  useHardwareBack(isOpen, close)
}
